import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  Timer,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Play,
} from 'lucide-react';
import type { WebhookLogsResponse, WebhookLogStats } from '../../api/webhook-logs';
import { fetchWebhookLogs, fetchWebhookLogStats } from '../../api/webhook-logs';
import WebhookLogDetailModal from './components/WebhookLogDetailModal';

const PAGE_SIZE = 50;
const REFRESH_INTERVAL_MS = 15_000;

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All Events' },
  { value: 'booking', label: 'Booking' },
  { value: 'item', label: 'Item' },
  { value: 'crew_member', label: 'Crew Member' },
  { value: 'unknown', label: 'Unknown' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'received', label: 'Received' },
  { value: 'processed', label: 'Processed' },
  { value: 'error', label: 'Error' },
  { value: 'recovery_failed', label: 'Recovery Failed' },
];

const SORT_OPTIONS = [
  { value: 'received_at', label: 'Time' },
  { value: 'event_type', label: 'Event Type' },
  { value: 'status', label: 'Status' },
  { value: 'processing_ms', label: 'Processing Time' },
];

function statusBadge(status: string) {
  // `error` is transient (the recovery sensor will retry these within ~30s).
  // `recovery_failed` is terminal — the sensor tried and couldn't recover;
  // needs human triage. Visually distinguish with a stronger warning color.
  const colors: Record<string, string> = {
    received: 'bg-water-100 text-water-800 dark:bg-water-900/30 dark:text-water-300',
    processed: 'bg-moss-100 text-moss-800 dark:bg-moss-900/30 dark:text-moss-300',
    error: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    recovery_failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };
  // Prettier label for the multi-word status
  const label = status === 'recovery_failed' ? 'recovery failed' : status;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300'}`}>
      {label}
    </span>
  );
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function WebhookLogDashboard() {
  const [data, setData] = useState<WebhookLogsResponse | null>(null);
  const [stats, setStats] = useState<WebhookLogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('received_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [offset, setOffset] = useState(0);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());
  const [nowTick, setNowTick] = useState<number>(Date.now());

  // `showFullSpinner` suppresses the big loading spinner on auto-refresh ticks
  // so the table doesn't flash every 15s — it only fires for the first load
  // and for filter/sort/pagination changes the user initiates.
  const showFullSpinnerRef = useRef(true);

  const loadStats = useCallback(() => {
    fetchWebhookLogStats()
      .then(setStats)
      .catch((err) => console.error('[WebhookLogs] Failed to load stats:', err));
  }, []);

  const loadLogs = useCallback(() => {
    if (showFullSpinnerRef.current) setLoading(true);
    fetchWebhookLogs({
      eventType: eventType || undefined,
      status: status || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      sort,
      sortDir,
      limit: PAGE_SIZE,
      offset,
    })
      .then((d) => {
        setData(d);
        setLastRefreshedAt(new Date());
      })
      .catch((err) => console.error('[WebhookLogs] Failed to load:', err))
      .finally(() => setLoading(false));
  }, [eventType, status, dateFrom, dateTo, sort, sortDir, offset]);

  // Initial load + reload on user-driven filter/sort/pagination changes.
  useEffect(() => {
    showFullSpinnerRef.current = true;
    loadStats();
    loadLogs();
  }, [loadStats, loadLogs]);

  // Auto-refresh tick. Runs every REFRESH_INTERVAL_MS while enabled. The
  // spinner is suppressed on these silent reloads so the UI doesn't flicker.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => {
      showFullSpinnerRef.current = false;
      loadStats();
      loadLogs();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, loadStats, loadLogs]);

  // "Last refreshed Xs ago" label — repaint every second.
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const secondsSinceRefresh = Math.max(
    0,
    Math.round((nowTick - lastRefreshedAt.getTime()) / 1000),
  );

  const logs = data?.logs ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageStart = offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div>
      {/* Stats cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 dark:text-stone-400">Total Today</p>
              <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">{stats?.totalToday ?? '--'}</p>
            </div>
            <Activity className="text-water-600" size={24} />
          </div>
        </div>
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 dark:text-stone-400">Success</p>
              <p className="text-2xl font-bold text-moss-600 mt-1">{stats?.successToday ?? '--'}</p>
            </div>
            <CheckCircle2 className="text-moss-600" size={24} />
          </div>
        </div>
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 dark:text-stone-400">Errors</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats?.errorsToday ?? '--'}</p>
            </div>
            <AlertTriangle className="text-red-500" size={24} />
          </div>
        </div>
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 dark:text-stone-400">Avg Processing</p>
              <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">
                {stats?.avgProcessingMs !== null && stats?.avgProcessingMs !== undefined ? `${stats.avgProcessingMs}ms` : '--'}
              </p>
            </div>
            <Timer className="text-purple-600" size={24} />
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border p-4 mb-4 flex items-center gap-3 flex-wrap">
        <select
          value={eventType}
          onChange={(e) => { setEventType(e.target.value); setOffset(0); }}
          className="border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
        >
          {EVENT_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
          className="border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
          placeholder="From"
          className="border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
          placeholder="To"
          className="border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
        />
        <div className="ml-auto flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
          {/* Auto-refresh toggle — styled to match the Newest button so it
              doesn't visually shout over the neutral filter controls. The
              status is conveyed by the icon (green pulse vs. play arrow),
              not a coloured background. */}
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            title={autoRefresh
              ? `Pause auto-refresh (currently every 15s, last update ${secondsSinceRefresh}s ago)`
              : 'Resume auto-refresh'}
            aria-label={autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'}
            className="px-2 py-1.5 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] text-xs inline-flex items-center gap-1.5 min-w-[88px] justify-center"
          >
            {autoRefresh ? (
              <>
                <span className="relative inline-flex h-2 w-2 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-moss-400 opacity-70" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-moss-500" />
                </span>
                <span className="tabular-nums">{secondsSinceRefresh}s ago</span>
              </>
            ) : (
              <>
                <Play size={12} className="flex-shrink-0" />
                <span>Paused</span>
              </>
            )}
          </button>
          <span>Sort</span>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setOffset(0); }}
            className="border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="px-2 py-1.5 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] text-xs"
          >
            {sortDir === 'asc' ? 'Oldest' : 'Newest'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 dark:bg-[#1e2520] text-stone-600 dark:text-stone-400 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Time</th>
              <th className="px-4 py-3 font-medium">Event Type</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Record PK</th>
              <th className="px-4 py-3 font-medium text-right">Processing</th>
              <th className="px-4 py-3 font-medium">Result</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-stone-500 dark:text-stone-400">
                  <Loader2 size={20} className="animate-spin inline mr-2" /> Loading webhook logs...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-stone-500 dark:text-stone-400">
                  No webhook logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr
                  key={log.id}
                  onClick={() => setSelectedLogId(log.id)}
                  className="hover:bg-moss-50/50 dark:hover:bg-[#242d28] cursor-pointer transition"
                >
                  <td className="px-4 py-3 text-stone-600 dark:text-stone-400 whitespace-nowrap">
                    {formatTime(log.received_at)}
                  </td>
                  <td className="px-4 py-3 font-medium text-bark-900 dark:text-[#e8ece9]">
                    {log.event_type ?? '--'}
                  </td>
                  <td className="px-4 py-3">{statusBadge(log.status)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-600 dark:text-stone-400">
                    {log.record_pk ?? '--'}
                  </td>
                  <td className="px-4 py-3 text-right text-stone-600 dark:text-stone-400">
                    {log.processing_ms !== null && log.processing_ms !== undefined ? `${log.processing_ms}ms` : '--'}
                  </td>
                  <td className="px-4 py-3 text-stone-600 dark:text-stone-400">
                    {log.dedup_result ?? '--'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="border-t px-4 py-3 flex items-center justify-between text-sm text-stone-600 dark:text-stone-400">
            <span>{pageStart}-{pageEnd} of {total.toLocaleString()}</span>
            <div className="flex items-center gap-2">
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="px-3 py-1.5 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} className="inline -mt-0.5" /> Previous
              </button>
              <button
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="px-3 py-1.5 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={14} className="inline -mt-0.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedLogId && (
        <WebhookLogDetailModal
          logId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
        />
      )}
    </div>
  );
}
